/**
 * Форма создания/правки типа номера (manager v3 §4 — управление фондом).
 * Менеджер/владелец заводит или редактирует тип: название, описание, цена,
 * вместимость (взрослые/дети/доп. места), удобства.
 *
 * Бэк: POST /v1/rooms/types (CreateRoomTypeRequest) — создание (нужен code +
 * tenant_id), PATCH /v1/rooms/types/{id} (UpdateRoomTypeRequest) — правка.
 * Гейт mgmt (#331). tenant_id берём из /v1/me.
 *
 * Цена вводится в рублях, на бэк уходит в копейках. Удобства — чекбоксы из
 * управляемого КАТАЛОГА удобств тенанта (GET /v1/amenities, правки Артёма
 * 2026-06-17): код + название + иконка из пака. room_types.amenities хранит
 * коды. Мобайл 375: один столбец.
 */
import { useMemo, useState } from 'react';
import { Button, Card } from '@hsc/ui';
import type {
  CreateRoomTypeInput,
  RoomTypeView,
  UpdateRoomTypeInput,
} from '@hsc/types';
import { DbIcon } from '../../shared/nav/DbIcon.js';
import { iconMap, useAmenities, useIcons } from './useAmenities.js';
import { useCreateRoomType, useUpdateRoomType } from './useRooms.js';

interface FormState {
  code: string;
  nameRu: string;
  descRu: string;
  priceRub: string;
  adults: string;
  children: string;
  extra: string;
  amenities: string[];
}

function initialState(rt?: RoomTypeView): FormState {
  if (!rt) {
    return {
      code: '',
      nameRu: '',
      descRu: '',
      priceRub: '',
      adults: '2',
      children: '0',
      extra: '0',
      amenities: [],
    };
  }
  return {
    code: rt.code,
    nameRu: rt.display_name?.ru ?? '',
    descRu: rt.description?.ru ?? '',
    priceRub: rt.base_price_kopecks ? String(rt.base_price_kopecks / 100) : '',
    adults: String(rt.capacity_adults),
    children: String(rt.capacity_children),
    extra: String(rt.capacity_extra_beds),
    amenities: [...(rt.amenities ?? [])],
  };
}

export interface RoomTypeFormProps {
  /** UUID тенанта (из /v1/me) — обязателен для создания. */
  tenantId: string | undefined;
  /** Редактируемый тип; отсутствует — режим создания. */
  editing?: RoomTypeView | undefined;
  onDone: () => void;
}

export function RoomTypeForm({ tenantId, editing, onDone }: RoomTypeFormProps): JSX.Element {
  const create = useCreateRoomType();
  const update = useUpdateRoomType();
  const amenitiesQ = useAmenities();
  const iconsQ = useIcons();
  const icons = useMemo(() => iconMap(iconsQ.data), [iconsQ.data]);
  const [s, setS] = useState<FormState>(() => initialState(editing));

  // Опции чекбоксов = активные удобства каталога + legacy-коды, уже
  // проставленные на типе, но отсутствующие в каталоге (чтобы не потерять).
  const catalogOptions = useMemo(() => {
    const cat = (amenitiesQ.data ?? []).filter((a) => a.is_active);
    const known = new Set(cat.map((a) => a.code));
    const opts = cat.map((a) => ({ code: a.code, name: a.name, iconId: a.icon_id }));
    for (const code of s.amenities) {
      if (!known.has(code)) opts.push({ code, name: code, iconId: null });
    }
    return opts;
  }, [amenitiesQ.data, s.amenities]);

  const isEdit = editing !== undefined;
  const pending = create.isPending || update.isPending;
  const isError = create.isError || update.isError;

  const priceKopecks = useMemo(() => {
    const v = Number.parseFloat(s.priceRub.replace(',', '.'));
    return Number.isFinite(v) && v >= 0 ? Math.round(v * 100) : 0;
  }, [s.priceRub]);

  const canSubmit =
    s.nameRu.trim().length > 0 &&
    (isEdit || (s.code.trim().length >= 3 && tenantId !== undefined)) &&
    !pending;

  const toggleAmenity = (code: string): void => {
    setS((prev) => ({
      ...prev,
      amenities: prev.amenities.includes(code)
        ? prev.amenities.filter((a) => a !== code)
        : [...prev.amenities, code],
    }));
  };

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;

    const adults = Number.parseInt(s.adults, 10) || 1;
    const children = Number.parseInt(s.children, 10) || 0;
    const extra = Number.parseInt(s.extra, 10) || 0;

    if (isEdit && editing) {
      const patch: UpdateRoomTypeInput = {
        display_name: { ru: s.nameRu.trim() },
        description: { ru: s.descRu.trim() },
        base_price_kopecks: priceKopecks,
        capacity_adults: adults,
        capacity_children: children,
        capacity_extra_beds: extra,
        amenities: s.amenities,
      };
      update.mutate({ id: editing.id, input: patch }, { onSuccess: onDone });
      return;
    }

    if (tenantId === undefined) return;
    const body: CreateRoomTypeInput = {
      tenant_id: tenantId,
      code: s.code.trim(),
      display_name: { ru: s.nameRu.trim() },
      description: { ru: s.descRu.trim() },
      base_price_kopecks: priceKopecks,
      currency: 'RUB',
      capacity_adults: adults,
      capacity_children: children,
      capacity_extra_beds: extra,
      amenities: s.amenities,
    };
    create.mutate(body, { onSuccess: onDone });
  };

  return (
    <Card data-testid="room-type-form">
      <div className="fleet-drawer__head">
        <h2 className="hsc-card__title">{isEdit ? 'Правка типа номера' : 'Новый тип номера'}</h2>
      </div>

      <form className="hsc-stack" onSubmit={handleSubmit}>
        {!isEdit && (
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rt-code">
              Код типа (латиницей, 3–32 символа)
            </label>
            <input
              id="rt-code"
              className="ta-input"
              data-testid="room-type-code"
              value={s.code}
              onChange={(e) => setS({ ...s, code: e.target.value })}
              placeholder="Например, standart"
              minLength={3}
              maxLength={32}
              required
            />
          </div>
        )}

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="rt-name">
            Название
          </label>
          <input
            id="rt-name"
            className="ta-input"
            data-testid="room-type-name"
            value={s.nameRu}
            onChange={(e) => setS({ ...s, nameRu: e.target.value })}
            placeholder="Например, Делюкс с террасой"
            required
          />
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="rt-desc">
            Описание
          </label>
          <textarea
            id="rt-desc"
            className="ta-input"
            data-testid="room-type-desc"
            value={s.descRu}
            onChange={(e) => setS({ ...s, descRu: e.target.value })}
            rows={3}
            placeholder="Панорама на море, балкон…"
          />
        </div>

        <div className="ta-field">
          <label className="hsc-input__label" htmlFor="rt-price">
            Базовая цена, ₽ / ночь
          </label>
          <input
            id="rt-price"
            type="number"
            className="ta-input"
            data-testid="room-type-price"
            value={s.priceRub}
            onChange={(e) => setS({ ...s, priceRub: e.target.value })}
            min={0}
            step={50}
            placeholder="6500"
          />
        </div>

        <div className="fleet-form-row">
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rt-adults">
              Взрослых
            </label>
            <input
              id="rt-adults"
              type="number"
              className="ta-input"
              data-testid="room-type-adults"
              value={s.adults}
              onChange={(e) => setS({ ...s, adults: e.target.value })}
              min={1}
              max={10}
            />
          </div>
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rt-children">
              Детей
            </label>
            <input
              id="rt-children"
              type="number"
              className="ta-input"
              data-testid="room-type-children"
              value={s.children}
              onChange={(e) => setS({ ...s, children: e.target.value })}
              min={0}
              max={10}
            />
          </div>
          <div className="ta-field">
            <label className="hsc-input__label" htmlFor="rt-extra">
              Доп. места
            </label>
            <input
              id="rt-extra"
              type="number"
              className="ta-input"
              data-testid="room-type-extra"
              value={s.extra}
              onChange={(e) => setS({ ...s, extra: e.target.value })}
              min={0}
              max={10}
            />
          </div>
        </div>

        <div className="ta-field">
          <span className="hsc-input__label">Удобства</span>
          {amenitiesQ.isLoading ? (
            <p className="hsc-input__hint">Загружаем каталог удобств…</p>
          ) : (
            <ul className="fleet-amenities-pick" data-testid="room-type-amenities">
              {catalogOptions.map((opt) => (
                <li key={opt.code}>
                  <label className="fleet-checkbox">
                    <input
                      type="checkbox"
                      checked={s.amenities.includes(opt.code)}
                      onChange={() => toggleAmenity(opt.code)}
                      data-testid={`room-type-amenity-${opt.code}`}
                    />
                    <span className="fleet-amenities-pick__icon">
                      <DbIcon icon={opt.iconId ? icons.get(opt.iconId) : undefined} size={18} />
                    </span>
                    <span>{opt.name}</span>
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="menu-form__submit">
          <Button type="submit" disabled={!canSubmit} data-testid="room-type-submit">
            {pending ? 'Сохраняем…' : isEdit ? 'Сохранить' : 'Создать тип'}
          </Button>
          <Button type="button" variant="ghost" onClick={onDone} data-testid="room-type-form-close">
            Назад
          </Button>
        </div>

        {isError && (
          <p role="alert" className="fleet-card__err" data-testid="room-type-error">
            Не удалось сохранить тип номера. Проверьте данные и попробуйте снова.
          </p>
        )}
      </form>
    </Card>
  );
}
